import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getLeadStatistics,
    getLeadQuickView,
} from '@/modules/Dashboard/services'
import { LeadQuickViewStatus, LeadRecord } from '@/modules/Dashboard/types'
import { paths } from '@/utils/path'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'
import {
    Users,
    UserCheck,
    UserX,
    Clock,
    Ban,
    UserPlus,
    BarChart3,
    List,
    ArrowRight,
} from 'lucide-react'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

type ViewMode = 'chart' | 'list'
type LeadStatus = 'unverified' | 'following' | 'matched' | 'un_matched' | 'spam'

const STATUS_CONFIG: Record<
    LeadStatus,
    {
        color: string
        bgColor: string
        borderColor: string
        icon: React.ReactNode
    }
> = {
    unverified: {
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: <Clock className="w-4 h-4" />,
    },
    following: {
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        icon: <UserPlus className="w-4 h-4" />,
    },
    matched: {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: <UserCheck className="w-4 h-4" />,
    },
    un_matched: {
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-200',
        icon: <UserX className="w-4 h-4" />,
    },
    spam: {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: <Ban className="w-4 h-4" />,
    },
}

const LeadsStatistics: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const [viewMode, setViewMode] = useState<ViewMode>('chart')
    const [activeStatus, setActiveStatus] = useState<LeadStatus>('unverified')

    // Calculate date range for last 30 days
    const dateRange = useMemo(
        () => ({
            start_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
            end_date: dayjs().format('YYYY-MM-DD'),
        }),
        []
    )

    // Fetch statistics for chart view
    const { data: statsData, isLoading: isStatsLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.LEAD_STATISTICS, dateRange],
        queryFn: () => getLeadStatistics(dateRange),
    })

    // Fetch quick view for list view
    const { data: quickViewData, isLoading: isQuickViewLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.LEAD_QUICK_VIEW, dateRange],
            queryFn: () => getLeadQuickView({ ...dateRange, limit: 3 }),
            enabled: viewMode === 'list',
        })

    const stats = statsData?.result
    const quickView = quickViewData?.result

    const statItems: { key: LeadStatus; label: string; value: number }[] = [
        {
            key: 'unverified',
            label: t('leads.unverified'),
            value: stats?.unverified || 0,
        },
        {
            key: 'following',
            label: t('leads.following'),
            value: stats?.following || 0,
        },
        {
            key: 'matched',
            label: t('leads.matched'),
            value: stats?.matched || 0,
        },
        {
            key: 'un_matched',
            label: t('leads.un_matched'),
            value: stats?.un_matched || 0,
        },
        {
            key: 'spam',
            label: t('leads.spam'),
            value: stats?.spam || 0,
        },
    ]

    const totalLeads = stats?.total || 0

    const handleViewAll = () => {
        navigate(paths.customerMkt)
    }

    const handleLeadClick = (contactId: number) => {
        // Navigate to contact detail - adjust path as needed
        navigate(`${paths.customerMkt}?contact_id=${contactId}`)
    }

    // Get current status data for list view
    const currentStatusData: LeadQuickViewStatus | undefined =
        quickView?.[activeStatus]

    // Status tabs for list view
    const statusTabs = useMemo(
        () =>
            statItems.map((item) => ({
                key: item.key,
                label: item.label,
                count:
                    viewMode === 'list'
                        ? quickView?.[item.key]?.total || 0
                        : item.value,
            })),
        [statItems, quickView, viewMode]
    )

    const renderChartView = () => (
        <div className="flex-1 flex flex-col">
            {/* Total Card */}
            <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3 mb-3 text-white"
                data-tour="leads-total"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-2xl font-bold">
                            {totalLeads.toLocaleString()}
                        </div>
                        <div className="text-xs text-blue-100">
                            {t('leads.total')}
                        </div>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg">
                        <Users className="w-6 h-6" />
                    </div>
                </div>
            </div>

            {/* Status Grid */}
            <div
                className="grid grid-cols-3 gap-2 flex-1"
                data-tour="leads-status-grid"
            >
                {statItems.slice(0, 3).map((item) => {
                    const config = STATUS_CONFIG[item.key]
                    return (
                        <div
                            key={item.key}
                            className={`${config.bgColor} rounded-lg p-2 border ${config.borderColor} flex flex-col justify-center`}
                            data-tour={`leads-${item.key}`}
                        >
                            <div className={`${config.color} mb-1`}>
                                {config.icon}
                            </div>
                            <div
                                className={`text-lg font-bold ${config.color}`}
                            >
                                {item.value.toLocaleString()}
                            </div>
                            <div className="text-[10px] text-gray-600 truncate">
                                {item.label}
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Bottom row - 2 items */}
            <div className="grid grid-cols-2 gap-2 mt-2">
                {statItems.slice(3).map((item) => {
                    const config = STATUS_CONFIG[item.key]
                    return (
                        <div
                            key={item.key}
                            className={`${config.bgColor} rounded-lg p-2 border ${config.borderColor} flex items-center gap-2`}
                            data-tour={`leads-${item.key}`}
                        >
                            <div className={`${config.color}`}>
                                {config.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div
                                    className={`text-sm font-bold ${config.color}`}
                                >
                                    {item.value.toLocaleString()}
                                </div>
                                <div className="text-[10px] text-gray-600 truncate">
                                    {item.label}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )

    const renderListView = () => (
        <>
            {/* Status Tabs - Horizontal scroll */}
            <div
                className="flex-shrink-0 mb-3 overflow-x-auto"
                style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#d1d5db transparent',
                }}
            >
                <div className="flex gap-1.5 min-w-max pb-1">
                    {statusTabs.map((tab) => {
                        const config = STATUS_CONFIG[tab.key]
                        const isActive = activeStatus === tab.key
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveStatus(tab.key)}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
                                    isActive
                                        ? `${config.bgColor} ${config.color} border ${config.borderColor}`
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {tab.label}
                                <span
                                    className={`text-[10px] ${isActive ? config.color : 'text-gray-500'}`}
                                >
                                    {tab.count}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* List */}
            <div className="space-y-1.5 flex-1">
                {isQuickViewLoading ? (
                    <>
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-14 rounded-lg" />
                        ))}
                    </>
                ) : currentStatusData?.records?.length === 0 ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                        {t('business.no_data')}
                    </div>
                ) : (
                    <>
                        {currentStatusData?.records
                            ?.slice(0, 3)
                            .map((record: LeadRecord) => (
                                <div
                                    key={record.contact_id}
                                    onClick={() =>
                                        handleLeadClick(record.contact_id)
                                    }
                                    className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                                >
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div
                                            className={`p-1.5 rounded-full flex-shrink-0 ${STATUS_CONFIG[activeStatus].bgColor}`}
                                        >
                                            <Users
                                                className={`w-3.5 h-3.5 ${STATUS_CONFIG[activeStatus].color}`}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-xs text-gray-900 truncate">
                                                {record.contact_name ||
                                                    record.contact_email}
                                            </div>
                                            <div className="text-[10px] text-gray-500 truncate">
                                                {record.contact_company ||
                                                    record.contact_website_referrer}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <span className="text-[10px] text-gray-400">
                                            {dayjs(record.created_at).format(
                                                'DD/MM'
                                            )}
                                        </span>
                                        <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                                    </div>
                                </div>
                            ))}
                        {/* Empty placeholders */}
                        {Array.from({
                            length: Math.max(
                                0,
                                3 - (currentStatusData?.records?.length || 0)
                            ),
                        }).map((_, i) => (
                            <div
                                key={`empty-${i}`}
                                className="h-[52px] bg-gray-50 rounded-lg border border-dashed border-gray-200"
                            />
                        ))}
                    </>
                )}
            </div>
        </>
    )

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <div>
                        <Typogaphy className="font-semibold text-primary">
                            {t('leads.title')}
                        </Typogaphy>
                        <span className="text-xs text-gray-500">
                            {t('leads.last_30_days')}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setViewMode('chart')}
                            className={`p-1.5 rounded ${viewMode === 'chart' ? 'bg-primary/10 text-primary' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            <BarChart3 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-primary/10 text-primary' : 'text-gray-400 hover:text-gray-600'}`}
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {isStatsLoading ? (
                    <div className="space-y-2 flex-1">
                        <Skeleton className="h-32 rounded-lg" />
                        <div className="space-y-1.5">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-5 rounded" />
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        {viewMode === 'chart'
                            ? renderChartView()
                            : renderListView()}

                        {/* View All */}
                        <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                            <button
                                onClick={handleViewAll}
                                className="text-xs text-primary hover:underline font-medium"
                            >
                                {t('widgets.view_all')} ({totalLeads})
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Paper>
    )
}

export default LeadsStatistics
